/**
 * Host-owned room control: what kind of room this is — which decides how it
 * mixes voices — and which pairs of participants agreed to trade places on
 * the ring.
 *
 * Placement is not here. Whether somebody walks or has their coordinate
 * computed for them follows the view they are in — the participants grid
 * delegates, the campfire does not — so it is a per-peer fact carried in
 * presence, not a room-wide setting the host picks.
 *
 * Head zoom is not here either: cropping a camera to a head costs nobody but
 * the person who chose it — their own uplink and their own view — so it is a
 * preference in `settings.ts` rather than a policy the host publishes.
 *
 * Pure state only — no transport, no React. The mode must come from the current
 * host; a swap may come from anybody, because it only exchanges two peers' own
 * places.
 */

import { z } from "zod";
import {
  applyAreaMove,
  forgetArea,
  RoomAreaSchema,
  type AreaMap,
} from "./roomAreas";
import { applySwap, forgetSwaps, hasSwap } from "./swaps";

/**
 * What kind of room this is, and the only thing that decides how it sounds.
 *
 * `campfire` is the scene with its distance falloff, `bubbles` the same scene
 * mixed in chains of neighbours, `plain` an ordinary call — tiles, every voice
 * at equal volume, nothing to walk — and `teams` the plain call divided into
 * three areas that hear themselves (`roomAreas.ts`).
 *
 * One field rather than a mode beside an acoustics, because they answered the
 * same question and could disagree. Room-wide, because two people are only in
 * the same conversation if they agree on what a conversation is.
 */
export const RoomModeSchema = z.enum(["campfire", "bubbles", "plain", "teams"]);
export type RoomMode = z.infer<typeof RoomModeSchema>;

/**
 * The order the four are offered in, wherever they are offered.
 *
 * The plainest first and the most particular last, because the list is read
 * from the top by somebody deciding: Plain is the room every other product
 * already taught them, Team games is a shape they can name, and the two
 * spatial ones are the ones that have to be tried to be understood. Campfire
 * is what the product is about and is still last — it is the answer, not the
 * question, and putting it first made the other three read as variations on a
 * choice already made.
 *
 * An order the schema cannot carry, so it is written once here and read
 * everywhere — a second list in a component is how two screens come to
 * disagree about what exists.
 *
 * That it holds every mode the schema does is a test, not a type: `satisfies`
 * would catch a mode that is not one, and say nothing about one left out.
 */
export const ROOM_MODES: readonly RoomMode[] = [
  "plain",
  "teams",
  "bubbles",
  "campfire",
];

/**
 * Where one media plane travels. Audio and video are chosen separately and out
 * of the same two answers, so the enum is defined once and named twice — the
 * two aliases are what the readers mean, and a third spelling of
 * `["mesh", "sfu"]` would be a second place for the vocabulary to drift.
 */
export const MediaTransportSchema = z.enum(["mesh", "sfu"]);
export type MediaTransport = z.infer<typeof MediaTransportSchema>;

/** The room-wide video plane the host has selected. */
export const VideoTransportSchema = MediaTransportSchema;
export type VideoTransport = z.infer<typeof VideoTransportSchema>;

/** The room-wide audio plane the host has selected. */
export const AudioTransportSchema = MediaTransportSchema;
export type AudioTransport = z.infer<typeof AudioTransportSchema>;

/**
 * What the room's row says about itself.
 *
 * These four are the room's configuration, and since #612 the row is the only
 * place they come from. The host writes it and then tells the room to read it
 * again; nobody hands anybody a value. What that buys is one answer rather than
 * two — the old path wrote the row best-effort *and* broadcast the value, so a
 * refused write left the room disagreeing with the record it would be rebuilt
 * from the next time somebody opened it.
 */
export const RoomSettingsSchema = z.object({
  /**
   * Which kind of room this is, and so which rules it mixes by.
   *
   * Defaulted rather than required so a row written before the column existed
   * still parses: one field's worth of default beats no settings at all.
   */
  roomMode: RoomModeSchema.default("campfire"),
  /** Video uses the mesh with TURN fallback or Cloudflare's SFU. */
  videoTransport: VideoTransportSchema.default("mesh"),
  /** Voices use the mesh or the same SFU session the video plane uses. */
  audioTransport: AudioTransportSchema.default("mesh"),
  /**
   * Whether anybody but the host is offered the room's journal
   * (`roomJournal.ts`). A decision about the room rather than about one
   * person's panel, which is why it is the room's row that holds it.
   */
  journalVisibleToAll: z.boolean().default(true),
  /**
   * Whether every voice in the room goes through ai-coustics. The host's
   * decision for the room, on unless they turn it off.
   */
  voiceCleaning: z.boolean().default(true),
});

export type RoomSettings = z.infer<typeof RoomSettingsSchema>;

/** How a write of the room's row went; only a refusal is said to the host. */
export type SettingsWrite = "written" | "refused" | "not-written";

/** What writes the room's row: the columns to set, and how it went. */
export type WriteSettings = (
  columns: Record<string, unknown>,
) => Promise<SettingsWrite>;

/**
 * Where everybody is standing, which is what the control channel is for.
 *
 * Separate from the settings above because the two have different owners and
 * different homes: this is about the people currently in the room and has no
 * record anywhere, so it is published between them; the settings are the room's
 * and are read from its row (#612).
 */
export const RoomPlacementSchema = z.object({
  /** Monotonic revision so late snapshots never overwrite fresher placement. */
  revision: z.number(),
  /** peerId -> peerId, symmetric: who traded places with whom. */
  swaps: z.record(z.string(), z.string()),
  /**
   * Who is standing in which area of a team-games room, where absence means the
   * hall (`roomAreas.ts`). Room state rather than a per-peer presence fact,
   * because a cap can only be held against one ordered stream: five clients
   * each publishing "I am in Team 1" about themselves is five people nobody
   * ever compared.
   *
   * Defaulted so a snapshot published by a client that predates the areas is a
   * room standing in the hall, not a dropped snapshot.
   */
  areas: z.record(z.string(), RoomAreaSchema).default({}),
});

export type RoomPlacement = z.infer<typeof RoomPlacementSchema>;

/** The whole of what a room is, as every screen in it reads it. */
export const RoomControlStateSchema = RoomPlacementSchema.extend(
  RoomSettingsSchema.shape,
);

export type RoomControlState = z.infer<typeof RoomControlStateSchema>;

export function createInitialRoomControl(): RoomControlState {
  return {
    revision: 0,
    swaps: {},
    areas: {},
    roomMode: "campfire",
    videoTransport: "mesh",
    audioTransport: "mesh",
    journalVisibleToAll: true,
    voiceCleaning: true,
  };
}

export const initialRoomControl = createInitialRoomControl();

/**
 * What travels on the room's control channel.
 *
 * **No configuration does.** Since #612 the four settings come from the room's
 * row, which every participant reads on an interval — so what is left here is
 * about the people currently standing in the room: who swapped with whom, who
 * walked into which area, and who can draw a mode the host is thinking of; and
 * one signal that the row is worth reading sooner.
 */
export const RoomControlBroadcastSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("swap-participant-places"),
    from: z.string(),
    peerId: z.string(),
  }),
  // The live half only. The settings used to travel in here too, and a late
  // joiner adopted whatever the publisher believed rather than what the room
  // was recorded as.
  z.object({
    type: z.literal("room-control-snapshot-published"),
    from: z.string(),
    state: RoomPlacementSchema,
  }),
  z.object({
    type: z.literal("move-to-room-area"),
    from: z.string(),
    area: RoomAreaSchema,
  }),
  /**
   * The room's settings have changed; read them again.
   *
   * Carries nothing but who said so, and that is the whole of it — a value
   * still comes from the row, for everybody, the host included. Every client
   * reads the row on an interval as well, and this is the faster of the two
   * rather than the authoritative one: the interval is what a room with
   * realtime down or clients on more than one version depends on, and this is
   * what makes the switch immediate for the clients that hear it (#612).
   */
  z.object({
    type: z.literal("room-settings-changed"),
    from: z.string(),
  }),
  /**
   * The host is thinking of making this room another kind, and would like
   * everybody to fetch that mode before anybody switches (#612).
   *
   * An intention rather than a state: nothing is recorded, nothing changes on
   * screen, and a proposal nobody commits is forgotten when the room empties.
   * The mode is named here because there is nowhere to read it from yet — the
   * row still says what the room *is*.
   */
  z.object({
    type: z.literal("propose-room-mode"),
    from: z.string(),
    roomMode: RoomModeSchema,
    proposalId: z.string(),
  }),
  /** "I have that mode and can draw it." Anybody may say it, about one proposal. */
  z.object({
    type: z.literal("room-mode-ready"),
    from: z.string(),
    proposalId: z.string(),
  }),
]);

export type RoomControlBroadcast = z.infer<typeof RoomControlBroadcastSchema>;

export type RoomControlMessage =
  RoomControlBroadcast | { type: "peer-left"; peerId: string };

export interface RoomControlContext {
  hostId: string | null;
}

/**
 * A setting the room has just been read as having.
 *
 * Not an event: nothing sends this. The hook reads the room's row and hands the
 * result here, so that "what the room is" is assembled in one place whether the
 * room was just opened or has just been told to read its settings again (#612).
 */
export function applyRoomSettings(
  state: RoomControlState,
  settings: RoomSettings,
): RoomControlState {
  const changed =
    state.roomMode !== settings.roomMode ||
    state.videoTransport !== settings.videoTransport ||
    state.audioTransport !== settings.audioTransport ||
    state.journalVisibleToAll !== settings.journalVisibleToAll ||
    state.voiceCleaning !== settings.voiceCleaning;
  return changed ? { ...state, ...settings } : state;
}

function reduceHostControl(
  state: RoomControlState,
  event: RoomControlBroadcast,
): RoomControlState {
  // Late joiners adopt the published placement, never an older revision. The
  // settings are not in it and are not the publisher's to give (#612).
  if (event.type !== "room-control-snapshot-published") return state;
  if (event.state.revision < state.revision) return state;
  return { ...state, ...event.state };
}

/**
 * The messages that change no room state, and mean to.
 *
 * The silence is the point. A proposal is an intention and an answer to one is
 * liveness, both of which reach the handshake in `useRoomControl` rather than
 * this reducer; and `room-settings-changed` says only that the room's row has
 * moved, which is answered by reading it. What the room *is* comes from that
 * row and from nothing that travels between clients (#612).
 */
const changesNoRoomState = (event: RoomControlMessage): boolean =>
  event.type === "propose-room-mode" ||
  event.type === "room-mode-ready" ||
  event.type === "room-settings-changed";

export function reduceRoomControl(
  state: RoomControlState,
  event: RoomControlMessage,
  ctx: RoomControlContext = { hostId: null },
): RoomControlState {
  if (event.type === "peer-left") {
    const swaps = hasSwap(state.swaps, event.peerId)
      ? forgetSwaps(state.swaps, event.peerId)
      : state.swaps;
    const areas: AreaMap = forgetArea(state.areas, event.peerId);
    if (swaps === state.swaps && areas === state.areas) return state;
    return { ...state, swaps, areas };
  }

  // A swap only exchanges two peers' own places, so anybody may ask for one.
  if (event.type === "swap-participant-places") {
    const swaps = applySwap(state.swaps, event.from, event.peerId);
    if (swaps === state.swaps) return state;
    return { ...state, swaps, revision: state.revision + 1 };
  }

  // A move only names the sender's own place, so anybody may ask for one — and
  // the capacity rule is applied here, on receipt, rather than at the press: a
  // client that has not yet heard about the fifth person must not be able to
  // admit a sixth.
  //
  // The revision advances even when the move is refused, so that every client
  // counts every move and their revisions stay in step. That is what lets the
  // host's snapshot correct the one client that applied its own move before it
  // heard anybody else's.
  if (event.type === "move-to-room-area") {
    const areas: AreaMap = applyAreaMove(state.areas, event.from, event.area);
    return { ...state, areas, revision: state.revision + 1 };
  }

  if (changesNoRoomState(event)) return state;

  // What is left is the host's published placement.
  if (!ctx.hostId || event.from !== ctx.hostId) return state;
  return reduceHostControl(state, event);
}
